import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function SalaryProcessing() {
  const [payroll, setPayroll] = useState([]);
  const [selected, setSelected] = useState(null);

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [summary, setSummary] = useState({
    staff_count: 0,
    total_salary: 0,
    draft: 0,
    processed: 0,
    overtime: 0,
    bonus: 0,
    deductions: 0,
  });

  useEffect(() => {
    loadPayroll();
  }, [month, year]);

  const loadPayroll = async () => {
    try {
      const res = await fetch(
        `/api/payroll?month=${month}&year=${year}`,
        {
          credentials: "include",
        },
      );

      const data = await res.json();

      if (data.status === "success") {
        setPayroll(data.data);
        setSummary(data.summary);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const processPayroll = async () => {
    try {
      const res = await fetch("/api/payroll/process", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          month,
          year,
        }),
      });

      const data = await res.json();

      alert(data.message);

      if (data.status === "success") {
        loadPayroll();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const updateField = (id, field, value) => {
    setPayroll((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              [field]: Number(value),
            }
          : p,
      ),
    );
  };

  const savePayroll = async (p) => {
    try {
      await fetch(`/api/payroll/${p.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bonus: p.bonus,
          deductions: p.deductions,
        }),
      });

      loadPayroll();
    } catch (err) {
      console.error(err);
    }
  };

  const viewPayroll = async (id) => {
    try {
      const res = await fetch(`/api/payroll/${id}`, {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setSelected(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-10">
        {/* ===== HEADER ===== */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-serif">Salary Processing</h1>

          <div className="flex gap-3">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-4 py-2 rounded-xl bg-black/40 border border-white/10"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>

            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-28 px-4 py-2 rounded-xl bg-black/40 border border-white/10"
            />

            <button
              onClick={processPayroll}
              className="px-6 py-2 rounded-xl bg-amber-400 text-black"
            >
              Process Payroll
            </button>
          </div>
        </div>

        {/* ===== SUMMARY ===== */}
        <div className="grid grid-cols-4 gap-5">
          <SummaryCard label="Total Staff" value={summary.staff_count} />

          <SummaryCard
            label="Total Salary"
            value={`₹${summary.total_salary.toFixed(2)}`}
          />

          <SummaryCard label="Draft" value={summary.draft} />

          <SummaryCard label="Processed" value={summary.processed} highlight />
        </div>

        {/* ===== PAYROLL TABLE ===== */}
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Employee</th>

                <th>Basic</th>

                <th>Commission</th>

                <th>Overtime</th>

                <th>Bonus</th>

                <th>Deductions</th>

                <th>Net Salary</th>

                <th>Status</th>

                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payroll.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-white/10 hover:bg-white/5"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p>{p.name}</p>

                      <p className="text-xs text-gray-400">{p.role}</p>
                    </div>
                  </td>

                  <td className="text-center">₹{p.basic_salary.toFixed(2)}</td>

                  <td className="text-center">₹{p.commission.toFixed(2)}</td>

                  <td className="text-center">
                    ₹{p.overtime_amount.toFixed(2)}
                  </td>

                  <td className="text-center">
                    <input
                      type="number"
                      value={p.bonus}
                      onChange={(e) =>
                        updateField(p.id, "bonus", e.target.value)
                      }
                      className="w-24 rounded bg-black border border-white/10 px-2 py-1"
                    />
                  </td>

                  <td className="text-center">
                    <input
                      type="number"
                      value={p.deductions}
                      onChange={(e) =>
                        updateField(p.id, "deductions", e.target.value)
                      }
                      className="w-24 rounded bg-black border border-white/10 px-2 py-1"
                    />
                  </td>

                  <td className="text-center font-semibold text-green-400">
                    ₹{p.net_salary.toFixed(2)}
                  </td>

                  <td className="text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        p.status === "Paid"
                          ? "bg-green-500/20 text-green-400"
                          : p.status === "Processed"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-yellow-500/20 text-yellow-400"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>

                  <td className="text-center space-x-2">
                    <button
                      onClick={() => savePayroll(p)}
                      className="text-green-400"
                    >
                      Save
                    </button>

                    <button
                      onClick={() => viewPayroll(p.id)}
                      className="text-amber-400"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ===== SALARY BREAKDOWN MODAL ===== */}
        {selected && (
          <Modal onClose={() => setSelected(null)}>
            <h2 className="text-xl font-serif mb-5">Payroll Details</h2>

            <div className="space-y-3">
              <Row label="Employee" value={selected.name} />

              <Row label="Role" value={selected.role} />

              <Row label="Month" value={`${selected.month}/${selected.year}`} />

              <Row label="Working Days" value={selected.working_days} />

              <Row label="Present" value={selected.present_days} />

              <Row label="Leave" value={selected.leave_days} />

              <Row label="Absent" value={selected.absent_days} />

              <hr className="border-white/10" />

              <Row label="Salary" value={`₹${selected.basic_salary}`} />

              <Row
                label="Commission"
                value={`₹${selected.commission_amount}`}
              />

              <Row label="Overtime" value={`₹${selected.overtime_amount}`} />

              <Row label="Bonus" value={`₹${selected.bonus}`} />

              <Row label="Deductions" value={`₹${selected.deductions}`} />

              <hr className="border-white/10" />

              <Row
                label="Net Salary"
                value={`₹${selected.net_salary}`}
                highlight
              />
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs text-gray-400 tracking-widest uppercase">{label}</p>
      <p
        className={`mt-3 text-2xl font-serif ${
          highlight ? "text-amber-400" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-400">{label}</span>
      <span className={highlight ? "text-amber-400 font-medium" : ""}>
        {value}
      </span>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}
